import React, { useState, useCallback, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid';
import {
  GridRenderCellParams,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel
} from '@mui/x-data-grid-pro';
import { useLocation, useNavigate } from 'react-router-dom';
import { SavedReportTypeList, StructureTypeCategory, GridFeatureModeConstant } from '../../../configs/enums';
import { Chip } from '../../../components/atoms/Chip';
import { TagCell } from '../../../components/molecules/TagCell';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { CustomDataGrid } from '../../../components/layouts/CustomDataGrid';
import { ActionPermissions, ColorProps, CustomPermission, ListType, NewReportDataType, OverwriteReportDataType, PropReportDataType, ReportResponse } from '../../../@types';
import { LogoImage, LogoImageBox } from './ClientContractList.styles';
import { useGetStructurePaginatedDataByType } from '../../../queries/structure-query';
import { useGetUserSystemPermissions } from '../../../queries/user-query';
import { isUserHasPermission } from '../../../configs/permissions';
import { Link } from '../../../components/atoms/Link';
import { useAddNewReport, useGetDefaultReportByReportType, useGetReportByCode, useUpdateReportByCode } from '../../../queries/report-query';
import { generateReportURL } from '../../../utils/report';
import { Snackbar } from '../../../components/atoms/Snackbar';
import { GridContainer } from '../../../styled/common.styles';
import { PLATFORM_NAME } from '../../../configs/common';
import { columnSelectionItems, defaultExcludedColumns, initialFilterModel } from '../../../constants/clientContract';
import { formatDate } from '../../../utils/common';

export const ClientContractList: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const reportCode = queryParams.get('savedReportCode') || '';

  const [reportViewCode, setReportViewCode] = useState<string>();
  const [reportColumnSelectionItems, setReportColumnSelectionItems] = useState<Array<ColumnSelectionItem>>();
  const [reportName, setReportName] = useState<string>('');
  const [newReportData, setNewReportData] = useState<PropReportDataType>();
  const [savedReportMessage, setSavedReportMessage] = useState<string>('');
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [fetchDataGrid, setFetchDataGrid] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rowCountState, setRowCountState] = useState(0);
  const [clientContractList, setClientContractList] = useState<Array<any>>([]);
  const [filterSearch, setFilterSearch] = useState(false);
  const [listId, setListId] = useState<string>(() => {
    if (reportCode) return `client-contract-list-${reportCode}`;
    return 'client-contract-list';
  });

  const useAddNewReportQuery = useAddNewReport();
  const useOverwriteReportQuery = useUpdateReportByCode(reportCode);
  const { data: reportQueryData, refetch: refetchGetReportByCodeQuery } = useGetReportByCode(reportCode);
  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const defaultReportData = useGetDefaultReportByReportType(SavedReportTypeList.ClientContractList);
  const { data: clientContractData, isLoading, refetch: refetchClientContractList, isRefetching } = useGetStructurePaginatedDataByType(
    StructureTypeCategory.System, 
    { page, pageSize, filterModel, sortModel }, 
    reportColumnSelectionItems ? reportColumnSelectionItems : columnSelectionItems, defaultExcludedColumns
  );

  useEffect(() => {
    setFirstLoad(true);
  }, []);

  useEffect(() => {
    if (reportCode) setListId(`client-contract-list-${reportCode}`);
    else setListId('client-contract-list');
  }, [reportCode]);

  useEffect(() => {
    if (!reportCode) {
      setFilterModel({
        items: [initialFilterModel]
      });
    }
  }, [reportCode]);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    setColumns([
      {
        field: 'id',
        headerName: 'ID',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => value?.toString()
      },
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        renderCell: (params: GridRenderCellParams) => 
          isUserHasPermission(ActionPermissions.Configure_Clients_or_Contracts_Edit, getUserPermissionsQuery.data) ?
            <Link href={`/configure/clients/${params.row.nodeTypeCode}/${params.row.id}`}>{params.row.name}</Link> :
            params.row.name
        ,
      },
      {
        field: 'nodeTypeCode',
        headerName: 'Type',
        width: 150,
        type: 'singleSelect',
        valueOptions: [
          { value: 'client', label: 'Client' },
          { value: 'contract', label: 'Contract' }
        ],
        renderCell: (params: GridRenderCellParams) => params.row.nodeTypeName
      },
      {
        field: 'code',
        headerName: 'Short Name',
        width: 150,
        renderCell: (params: GridRenderCellParams) => 
          isUserHasPermission(ActionPermissions.Configure_Clients_or_Contracts_Edit, getUserPermissionsQuery.data) ?
            <Link href={`/configure/clients/${params.row.nodeTypeCode}/${params.row.id}`}>{params.row.code}</Link> :
            params.row.code
      },
      {
        field: 'logoPic',
        headerName: 'Logo',
        width: 75,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.logoPic && params.row.logoPic !== null)
            return (
              <LogoImageBox>
                <LogoImage src={params.row.logoPic} alt={params.row.code} />
              </LogoImageBox>
            );
        },
        filterable: false
      },
      {
        field: 'clientContractTags',
        headerName: 'Tags',
        width: 300,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.clientContractTags)
            return (
              <TagCell tags={params.row.clientContractTags} />
            );
          else
            return '';
        },
      },
      {
        field: 'isActive',
        headerName: 'Active',
        type: 'singleSelect',
        width: 100,
        valueOptions: [
          { value: 'any', label: 'Any' },
          { value: 'true', label: 'Yes' },
          { value: 'false', label: 'No' }
        ],
        renderCell: (params: GridRenderCellParams) => params.row.isActive ? <Chip label="YES" color={ColorProps.Success} /> : <Chip label="NO" color={ColorProps.Error} />
      },
      {
        field: 'createdAt',
        headerName: 'CreatedAt',
        type: 'date',
        width: 200,
        valueGetter: (_value, row) => new Date(row?.createdAt),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        type: 'string',
        width: 200
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        type: 'date',
        width: 200,
        valueGetter: (_value, row) => row?.updatedAt && new Date(row?.updatedAt),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'updatedBy',
        headerName: 'Updated By',
        type: 'string',
        width: 200
      },
      {
        field: 'allAddresses',
        headerName: 'Addresses',
        width: 250,
        type: 'string'
      },
      {
        field: 'allContacts',
        headerName: 'Contacts',
        width: 200,
        type: 'string'
      }
    ]); 
  }, [permissions]);
  
  useEffect(() => {
    !reportCode && defaultReportData.refetch(); 
  }, [SavedReportTypeList.ClientContractList]);

  useEffect(() => {
    if (!reportCode && defaultReportData.data && defaultReportData.data?.length > 0) {
      sessionStorage.setItem('client-contract-list', JSON.stringify(defaultReportData.data[0].dataGridProperties?.filterModel));
      setFilterModel(defaultReportData.data[0].dataGridProperties?.filterModel);
      setSortModel(defaultReportData.data[0].dataGridProperties?.sortModel);
      setReportColumnSelectionItems(defaultReportData.data[0].columnSelection);
      setPageSize(defaultReportData.data[0].dataGridProperties?.pageSize);
      defaultReportData.data[0].dataGridProperties?.columnOrder && setColumns(defaultReportData.data[0].dataGridProperties?.columnOrder);
      
      if (defaultReportData.data[0].dataGridProperties?.columnOrder && columns) {
        const updatedColumns = defaultReportData.data[0].dataGridProperties?.columnOrder.map(orderCol => {
          const originalColumn = columns.find(col => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }
    }   
  }, [defaultReportData.data, reportCode]);

  useEffect(() => {
    if (reportCode) {
      refetchGetReportByCodeQuery();
      setReportViewCode(reportCode);
    }
  }, [reportCode, fetchDataGrid]);

  useEffect(() => {
    if (reportCode && reportQueryData && firstLoad) {
      document.title = `${reportQueryData.name} (${reportCode}) | List Clients | ${PLATFORM_NAME}`;
      
      const storedFilterModel = sessionStorage.getItem(`client-contract-list-${reportCode}`);
      setFilterModel(storedFilterModel ? JSON.parse(storedFilterModel) : reportQueryData.dataGridProperties.filterModel);
      setSortModel(reportQueryData.dataGridProperties?.sortModel);
      setReportColumnSelectionItems(reportQueryData.columnSelection);
      setPageSize(reportQueryData.dataGridProperties?.pageSize);
      setReportName(reportQueryData.name);
      setIsDefault(reportQueryData.isDefault);

      if (reportQueryData.dataGridProperties?.columnOrder && columns) {
        const updatedColumns = reportQueryData.dataGridProperties.columnOrder.map(orderCol => {
          const originalColumn = columns.find(col => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });

        setColumns(updatedColumns);
      }
      
      setFirstLoad(false);
    }   
  }, [reportQueryData, reportCode, columns, firstLoad]);

  useEffect(() => {
    setNewReportData({
      dataGridProperties: {
        filterModel: filterModel,
        pageSize: pageSize,
        sortModel: sortModel || [],
        columnOrder: columns
      },
      reportTypeName: SavedReportTypeList.ClientContractList,
      reportName: reportName,
      isDefault: reportQueryData?.isDefault,
      id: reportQueryData?.id,
      reportOwner: reportQueryData?.createdBy,
      columnSelection: reportColumnSelectionItems
    });
  }, [filterModel, pageSize, isDefault, reportName, sortModel, columns]);

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    if (!filterModel.items.find(filter => filter.field === 'nodeTypeCode')) {
      filterModel.items.push(initialFilterModel);
    }

    setFilterModel(filterModel);
  }, []);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  useEffect(() => {
    !reportCode && refetchClientContractList();
  }, []);

  useEffect(() => {
    if (page || pageSize || filterModel || sortModel || !reportCode || (reportCode && reportQueryData)) {
      refetchClientContractList();
    }
  }, [page, pageSize, filterModel, sortModel, isDefault, reportCode, reportQueryData, filterSearch]);

  useEffect(() => {
    if (clientContractData) {
      setClientContractList(clientContractData.data || []);
      setRowCountState(clientContractData.total || 0);
    }
  }, [clientContractData]);

  const handleColumnsChange = (items: Array<ColumnSelectionItem>) => {
    setReportColumnSelectionItems(items);
  };

  const savedReport = async (newReportData: NewReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useAddNewReportQuery.mutateAsync(newReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Created');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const overwriteReport = async (overwriteReportData: OverwriteReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useOverwriteReportQuery.mutateAsync(overwriteReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Updated');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true); 
    navigate(reportURL, { replace: true });
  };

  return (
    <GridContainer>
      <CustomDataGrid
        id={listId}
        columns={columns}
        rows={clientContractList}
        columnSelectionItems={reportColumnSelectionItems ? reportColumnSelectionItems : columnSelectionItems}
        exportFileName={ListType.ClientContractList}
        enableCSVExport={isUserHasPermission(ActionPermissions.Configure_Clients_or_Contracts_Export, permissions)}
        filterMode={GridFeatureModeConstant.server}
        onFilterModelChange={(filterModel: GridFilterModel) => {
          onFilterChange(filterModel);
        }}
        loading={isLoading || isRefetching}
        rowCount={rowCountState}
        rowsPerPageOptions={[25, 50, 100]}
        pagination={true}
        page={page}
        pageSize={pageSize}
        paginationMode={GridFeatureModeConstant.server}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
        }}
        sortModel={sortModel}
        sortingMode={GridFeatureModeConstant.server}
        onSortModelChange={handleSortModelChange}
        selectionModel={selectionModel}
        onSelectionModelChange={handleSelectionModelChange}
        componentsProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true }
          }
        }}
        filterModel={filterModel}
        structureTypeCategory={StructureTypeCategory.System}
        newReportData={newReportData} 
        handleSavedReport={(newReportData: NewReportDataType) => savedReport(newReportData)}
        handleOverwriteReport={(overwriteReportData: OverwriteReportDataType) => overwriteReport(overwriteReportData)}
        enableSavedReport={true}
        reportCode={reportViewCode}
        handleColumnsChange={handleColumnsChange}
        handleRefetch={() => setFetchDataGrid(!fetchDataGrid)}
        getRowHeight={() => 'auto'}
        defaultExcludedColumns={defaultExcludedColumns}
        setColumns={setColumns}
        filtersSearch={() => setFilterSearch(!filterSearch)}
      />
      <Grid>
        <Snackbar  
          open={openToast} 
          autoHideDuration={4000} 
          message={savedReportMessage ? savedReportMessage : 'Successfully Created'}
          onClose={()=> setOpenToast(false)}
        />
      </Grid>
    </GridContainer>
  );
};